import React, { useState, useEffect, useRef, useCallback } from 'react'
import { Link, useParams, useSearchParams, useNavigate } from 'react-router-dom'
import {
  ShieldCheckIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ChevronRightIcon,
  ChevronDownIcon,
  ChevronLeftIcon,
  ArrowRightIcon,
  ArrowUpTrayIcon,
  ArrowUturnLeftIcon,
  PaperClipIcon,
  LockClosedIcon,
  ChatBubbleLeftRightIcon,
  EyeIcon,
  PlusIcon,
  DocumentArrowDownIcon,
  XMarkIcon,
  MagnifyingGlassIcon,
  UserIcon,
  UserGroupIcon,
} from '@heroicons/react/24/outline'
import { Button } from '../../components/ui/button'
import PatronScreeningToolModal from './components/PatronScreeningToolModal'
import SearchAuditPanel from './components/SearchAuditPanel'
import PatronIdBadge from '../../components/PatronIdBadge'
import { resolvePatronIdentity } from '../../utils/patronIdentity'
import { Card, CardContent } from '../../components/ui/card'
import { getLocalApiUrl, authFetch } from '../../config/api'
import { isPatronScreeningPrototypeEnabled } from '../../config/featureFlags'
import { usePatronScreeningToolEnabled } from '../../hooks/usePatronScreeningToolEnabled'
import { useAuth } from '../../contexts/AuthContext'
import HelpScreenButton from '../../components/help/HelpScreenButton'
import ScreenHelpPanel from '../../components/help/ScreenHelpPanel'

const ECDD_DETAIL_HELP_SCREEN_KEY = 'page:compliance-safe/ecdd/detail'

// Task template config — task status/assignments loaded from API
const INITIAL_BUCKETS = [
  {
    id: 'sof',
    title: 'Source of Funds / Source of Wealth',
    icon: '💰',
    description: "Identify where the customer's money comes from — both for specific transactions and their general wealth.",
    items: [
      { id: 'sof_1', label: 'Request source of funds declaration from patron', guidance: 'Ask the patron to explain the source of funds for recent gaming activity.', mandatory: true, status: 'not_started', assignedTo: null, completedDate: null, findings: '', docs: [], dataSource: null },
      { id: 'sof_2', label: 'Verify source of funds documentation', guidance: 'Review any supporting documents provided.', mandatory: true, status: 'not_started', assignedTo: null, completedDate: null, findings: '', docs: [], dataSource: null },
      { id: 'sof_3', label: 'Assess source of wealth (overall)', guidance: 'For PEPs, assess the customer\'s overall wealth.', mandatory: true, status: 'not_started', assignedTo: null, completedDate: null, findings: '', docs: [], dataSource: null },
    ],
  },
  {
    id: 'verify',
    title: 'Additional Identification / Re-verification',
    icon: '🔍',
    description: 'Collect additional identity information or re-verify existing KYC.',
    items: [
      { id: 'ver_1', label: 'Re-verify existing KYC documents', guidance: 'Confirm the current KYC record is still accurate.', mandatory: false, status: 'not_started', assignedTo: null, completedDate: null, findings: '', docs: [], dataSource: null, isKyc: true, kycDocs: [] },
      { id: 'ver_2', label: 'Collect additional identification (occupation, employer)', guidance: 'For PEPs, collect details of their political role.', mandatory: true, status: 'not_started', assignedTo: null, completedDate: null, findings: '', docs: [], dataSource: null, isKyc: true, kycDocs: [] },
    ],
  },
  {
    id: 'txn',
    title: 'Transaction Analysis',
    icon: '📊',
    description: 'Detailed analysis of the customer\'s gaming and transaction history.',
    items: [
      { id: 'txn_1', label: 'Review payout history (last 12 months)', guidance: 'View payout history from the system. Filter, export to XLS and attach if needed.', mandatory: false, status: 'not_started', assignedTo: null, completedDate: null, findings: '', docs: [], dataSource: 'payout_history' },
      { id: 'txn_2', label: 'Review gaming session data', guidance: 'View gaming session data from the system.', mandatory: false, status: 'not_started', assignedTo: null, completedDate: null, findings: '', docs: [], dataSource: 'gaming_session' },
    ],
  },
  {
    id: 'approval',
    title: 'Senior Management Approval',
    icon: '✅',
    description: 'For foreign PEPs: senior management must approve whether to continue providing services.',
    items: [
      { id: 'app_1', label: 'Prepare recommendation for management', guidance: 'Summarise findings from all ECDD measures.', mandatory: true, status: 'not_started', assignedTo: null, completedDate: null, findings: '', docs: [] },
      { id: 'app_2', label: 'Obtain senior management approval', guidance: 'Submit the completed ECDD package for review.', mandatory: true, status: 'not_started', assignedTo: null, completedDate: null, findings: '', docs: [] },
    ],
  },
  {
    id: 'custom',
    title: 'Custom measures',
    icon: '➕',
    description: 'Additional ECDD steps you add for this review.',
    items: [],
  },
]

const ITEM_STATUS = {
  not_started: { label: 'Not Started', color: '#6B7280', bg: '#f8fafc', ring: '#e5e7eb' },
  in_progress: { label: 'In Progress', color: '#ca8a04', bg: '#fefce8', ring: '#fef08a' },
  done: { label: 'Done', color: '#16a34a', bg: '#f0fdf4', ring: '#bbf7d0' },
  na: { label: 'N/A', color: '#6B7280', bg: '#f3f4f6', ring: '#e5e7eb' },
}

/** Required checklist rows are satisfied by Done or N/A (not applicable). */
function isMandatoryResolved(status) {
  return status === 'done' || status === 'na'
}

/** Normalize ecdd_records.buckets_snapshot (array or { buckets, decision, … }) into UI state. */
function hydrateEcddDraftFromRecord(record) {
  if (!record) return null
  const snap = record.bucketsSnapshot
  let nextBuckets = null
  let decision = record.resolution || null
  let rationale = null

  if (Array.isArray(snap) && snap.length) {
    nextBuckets = snap
  } else if (snap && typeof snap === 'object' && Array.isArray(snap.buckets) && snap.buckets.length) {
    nextBuckets = snap.buckets
    decision = snap.decision || decision
    rationale = snap.decisionRationale || null
  }

  if (!nextBuckets) return null
  return { buckets: nextBuckets, decision, rationale }
}

/** API notes_log → display rows (supports legacy { date, who, action }). */
function normalizeEcddNotes(raw) {
  if (!raw || !Array.isArray(raw)) return []
  return raw
    .filter((n) => n && (n.text != null || n.action != null))
    .map((n, i) => ({
      id: n.id || `note_${i}`,
      ts: n.ts || n.date || '',
      who: n.who || 'User',
      text: n.text != null ? String(n.text) : String(n.action || ''),
    }))
}

function formatEcddNoteTs(ts) {
  if (!ts) return '—'
  try {
    const d = new Date(ts)
    if (Number.isNaN(d.getTime())) return String(ts)
    return d.toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    })
  } catch {
    return String(ts)
  }
}

function Badge({ bg, color, border, children }) {
  return (
    <span
      className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold whitespace-nowrap"
      style={{ backgroundColor: bg, color, border: border ? `1px solid ${border}` : undefined }}
    >
      {children}
    </span>
  )
}


function AddCustomItemModal({ open, onClose, onAdd }) {
  const [label, setLabel] = useState('')
  const [guidance, setGuidance] = useState('')
  const [mandatory, setMandatory] = useState(false)

  useEffect(() => {
    if (open) {
      setLabel('')
      setGuidance('')
      setMandatory(false)
    }
  }, [open])

  const handleSubmit = useCallback(() => {
    const t = label.trim()
    if (!t) return
    onAdd({ label: t, guidance: guidance.trim(), mandatory })
    onClose()
  }, [label, guidance, mandatory, onAdd, onClose])

  if (!open) return null
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} role="presentation" />
      <div className="relative bg-white rounded-xl shadow-xl w-full max-w-md flex flex-col border border-gray-100">
        <div className="flex items-center justify-between p-4 border-b">
          <h3 className="text-lg font-semibold text-psg-teal">Add custom checklist item</h3>
          <button type="button" onClick={onClose} className="p-1 rounded hover:bg-gray-100" aria-label="Close">
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>
        <div className="p-4 space-y-3">
          <div>
            <label htmlFor="ecdd-custom-label" className="block text-sm font-semibold text-gray-600 mb-1">Title</label>
            <input
              id="ecdd-custom-label"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="e.g. Obtain bank statements"
              className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm text-gray-800 outline-none focus:border-gray-300"
              autoFocus
            />
          </div>
          <div>
            <label htmlFor="ecdd-custom-guidance" className="block text-sm font-semibold text-gray-600 mb-1">Guidance (optional)</label>
            <textarea
              id="ecdd-custom-guidance"
              value={guidance}
              onChange={(e) => setGuidance(e.target.value)}
              placeholder="Instructions for staff completing this step…"
              rows={3}
              className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm text-gray-800 resize-y outline-none focus:border-gray-300"
            />
          </div>
          <label className="flex items-center gap-2 cursor-pointer text-sm text-gray-700">
            <input
              type="checkbox"
              checked={mandatory}
              onChange={(e) => setMandatory(e.target.checked)}
              className="rounded border-gray-300"
            />
            Required for this ECDD
          </label>
        </div>
        <div className="p-4 border-t bg-gray-50 flex justify-end gap-2 rounded-b-xl">
          <Button type="button" variant="outline" size="sm" onClick={onClose}>Cancel</Button>
          <Button type="button" size="sm" className="bg-psg-teal hover:bg-psg-teal-700" onClick={handleSubmit} disabled={!label.trim()}>
            Add item
          </Button>
        </div>
      </div>
    </div>
  )
}

function PayoutHistoryModal({ open, onClose, memberId, memberName, onExportAndAttach }) {
  const [filters, setFilters] = useState({
    dateFrom: '',
    dateTo: '',
    venue: '',
    minAmount: '',
    maxAmount: '',
    method: '',
    reference: '',
  })
  const [exporting, setExporting] = useState(false)
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(false)
  const [loadError, setLoadError] = useState('')

  useEffect(() => {
    if (!open || !memberId) return
    let cancelled = false
    ;(async () => {
      setLoading(true)
      setLoadError('')
      try {
        const r = await authFetch(getLocalApiUrl(`/api/ecdd/member/${encodeURIComponent(memberId)}/payout-history`))
        const j = await r.json().catch(() => ({}))
        if (cancelled) return
        if (!r.ok || !j.success) throw new Error(j.error || 'Failed to load payouts')
        setRows(Array.isArray(j.data) ? j.data : [])
      } catch (e) {
        if (!cancelled) {
          setRows([])
          setLoadError(e.message || 'Failed to load payouts')
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => { cancelled = true }
  }, [open, memberId])

  const filtered = rows.filter((r) => {
    if (filters.dateFrom && r.date < filters.dateFrom) return false
    if (filters.dateTo && r.date > filters.dateTo) return false
    if (filters.venue && !r.venue.toLowerCase().includes(filters.venue.toLowerCase())) return false
    if (filters.minAmount && r.amount < Number(filters.minAmount)) return false
    if (filters.maxAmount && r.amount > Number(filters.maxAmount)) return false
    if (filters.method && r.method.toLowerCase() !== filters.method.toLowerCase()) return false
    if (filters.reference && !r.reference.toLowerCase().includes(filters.reference.toLowerCase())) return false
    return true
  })

  const totalAmount = filtered.reduce((sum, r) => sum + r.amount, 0)

  const handleExport = () => {
    setExporting(true)
    const headers = ['Date', 'Venue', 'Amount', 'Reference', 'Method']
    const csvRows = filtered.map((r) => [r.date, r.venue, r.amount, r.reference, r.method])
    const csv = [headers.join(','), ...csvRows.map((row) => row.join(','))].join('\n')
    const blob = new Blob([csv], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `Payout_History_${memberId || 'Member'}.csv`
    a.click()
    URL.revokeObjectURL(url)
    setExporting(false)
    onExportAndAttach && onExportAndAttach()
    onClose()
  }

  const clearFilters = () => setFilters({ dateFrom: '', dateTo: '', venue: '', minAmount: '', maxAmount: '', method: '', reference: '' })

  if (!open) return null
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-xl w-full max-w-6xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between p-4 border-b">
          <h3 className="text-lg font-semibold text-psg-teal">Payout History (Last 12 Months)</h3>
          <button type="button" onClick={onClose} className="p-1 rounded hover:bg-gray-100"><XMarkIcon className="w-5 h-5" /></button>
        </div>
        <div className="p-4 border-b bg-gray-50">
          <div className="text-xs text-gray-500 mb-3">Member: {memberName || memberId || '—'}</div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-7 gap-3">
            <input
              type="date"
              placeholder="Date from"
              value={filters.dateFrom}
              onChange={(e) => setFilters((f) => ({ ...f, dateFrom: e.target.value }))}
              className="input-field text-sm"
            />
            <input
              type="date"
              placeholder="Date to"
              value={filters.dateTo}
              onChange={(e) => setFilters((f) => ({ ...f, dateTo: e.target.value }))}
              className="input-field text-sm"
            />
            <input
              placeholder="Venue"
              value={filters.venue}
              onChange={(e) => setFilters((f) => ({ ...f, venue: e.target.value }))}
              className="input-field text-sm"
            />
            <input
              type="number"
              placeholder="Min amount"
              value={filters.minAmount}
              onChange={(e) => setFilters((f) => ({ ...f, minAmount: e.target.value }))}
              className="input-field text-sm"
            />
            <input
              type="number"
              placeholder="Max amount"
              value={filters.maxAmount}
              onChange={(e) => setFilters((f) => ({ ...f, maxAmount: e.target.value }))}
              className="input-field text-sm"
            />
            <input
              placeholder="Method"
              value={filters.method}
              onChange={(e) => setFilters((f) => ({ ...f, method: e.target.value }))}
              className="input-field text-sm"
            />
            <input
              placeholder="Reference"
              value={filters.reference}
              onChange={(e) => setFilters((f) => ({ ...f, reference: e.target.value }))}
              className="input-field text-sm"
            />
          </div>
          <div className="flex justify-between items-center mt-3">
            <button type="button" onClick={clearFilters} className="text-xs text-psg-teal hover:underline">Clear filters</button>
            <span className="text-xs text-gray-500">
              {filtered.length} payout(s) · Total: ${totalAmount.toLocaleString()}
            </span>
          </div>
        </div>
        <div className="flex-1 overflow-auto p-4 min-h-0">
          {loading && <div className="py-16 text-center text-gray-500">Loading payout history…</div>}
          {!loading && loadError && (
            <div className="py-16 text-center text-red-600 text-sm">{loadError}</div>
          )}
          {!loading && !loadError && (
            <>
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 sticky top-0 z-10">
                  <tr>
                    <th className="px-4 py-3 text-left font-medium text-gray-600">Date</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-600">Venue</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-600">Amount</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-600">Reference</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-600">Method</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((r) => (
                    <tr key={r.id} className="border-b hover:bg-gray-50">
                      <td className="px-4 py-3">{r.date}</td>
                      <td className="px-4 py-3">{r.venue}</td>
                      <td className="px-4 py-3 font-mono">${r.amount.toLocaleString()}</td>
                      <td className="px-4 py-3 font-mono">{r.reference}</td>
                      <td className="px-4 py-3">{r.method}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {filtered.length === 0 && (
                <div className="py-16 text-center text-gray-500">
                  {rows.length === 0 ? 'No payouts in the last 12 months for this member.' : 'No payouts match filters'}
                </div>
              )}
            </>
          )}
        </div>
        <div className="p-4 border-t flex justify-between items-center bg-gray-50">
          <span className="text-sm text-gray-600">{filtered.length} row(s) shown</span>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={onClose}>Close</Button>
            <Button size="sm" onClick={handleExport} disabled={exporting || filtered.length === 0}>
              <DocumentArrowDownIcon className="w-4 h-4 mr-1" />
              {exporting ? 'Exporting…' : 'Export to CSV & Attach'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}

function GamingSessionModal({ open, onClose, memberId, memberName, onExportAndAttach }) {
  const [filters, setFilters] = useState({
    dateFrom: '',
    dateTo: '',
    venue: '',
    minDuration: '',
    maxDuration: '',
    minCashIn: '',
    maxCashIn: '',
    egm: '',
  })
  const [exporting, setExporting] = useState(false)
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(false)
  const [loadError, setLoadError] = useState('')

  useEffect(() => {
    if (!open || !memberId) return
    let cancelled = false
    ;(async () => {
      setLoading(true)
      setLoadError('')
      try {
        const r = await authFetch(getLocalApiUrl(`/api/ecdd/member/${encodeURIComponent(memberId)}/gaming-sessions`))
        const j = await r.json().catch(() => ({}))
        if (cancelled) return
        if (!r.ok || !j.success) throw new Error(j.error || 'Failed to load gaming sessions')
        setRows(Array.isArray(j.data) ? j.data : [])
      } catch (e) {
        if (!cancelled) {
          setRows([])
          setLoadError(e.message || 'Failed to load gaming sessions')
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => { cancelled = true }
  }, [open, memberId])

  const filtered = rows.filter((r) => {
    if (filters.dateFrom && r.date < filters.dateFrom) return false
    if (filters.dateTo && r.date > filters.dateTo) return false
    if (filters.venue && !r.venue.toLowerCase().includes(filters.venue.toLowerCase())) return false
    if (filters.minDuration && r.duration < Number(filters.minDuration)) return false
    if (filters.maxDuration && r.duration > Number(filters.maxDuration)) return false
    if (filters.minCashIn && r.cashIn < Number(filters.minCashIn)) return false
    if (filters.maxCashIn && r.cashIn > Number(filters.maxCashIn)) return false
    if (filters.egm && !r.egm.toLowerCase().includes(filters.egm.toLowerCase())) return false
    return true
  })

  const totalTurnover = filtered.reduce((sum, r) => sum + r.turnover, 0)
  const totalCashIn = filtered.reduce((sum, r) => sum + r.cashIn, 0)

  const handleExport = () => {
    setExporting(true)
    const headers = ['Date', 'Venue', 'Duration (min)', 'Cash In', 'Turnover', 'EGM']
    const csvRows = filtered.map((r) => [r.date, r.venue, r.duration, r.cashIn, r.turnover, r.egm])
    const csv = [headers.join(','), ...csvRows.map((row) => row.join(','))].join('\n')
    const blob = new Blob([csv], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `Gaming_Sessions_${memberId || 'Member'}.csv`
    a.click()
    URL.revokeObjectURL(url)
    setExporting(false)
    onExportAndAttach && onExportAndAttach()
    onClose()
  }

  const clearFilters = () =>
    setFilters({ dateFrom: '', dateTo: '', venue: '', minDuration: '', maxDuration: '', minCashIn: '', maxCashIn: '', egm: '' })

  if (!open) return null
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-xl w-full max-w-6xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between p-4 border-b">
          <h3 className="text-lg font-semibold text-psg-teal">Gaming Session Data (Last 12 Months)</h3>
          <button type="button" onClick={onClose} className="p-1 rounded hover:bg-gray-100"><XMarkIcon className="w-5 h-5" /></button>
        </div>
        <div className="p-4 border-b bg-gray-50">
          <div className="text-xs text-gray-500 mb-3">Member: {memberName || memberId || '—'}</div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-8 gap-3">
            <input
              type="date"
              placeholder="Date from"
              value={filters.dateFrom}
              onChange={(e) => setFilters((f) => ({ ...f, dateFrom: e.target.value }))}
              className="input-field text-sm"
            />
            <input
              type="date"
              placeholder="Date to"
              value={filters.dateTo}
              onChange={(e) => setFilters((f) => ({ ...f, dateTo: e.target.value }))}
              className="input-field text-sm"
            />
            <input
              placeholder="Venue"
              value={filters.venue}
              onChange={(e) => setFilters((f) => ({ ...f, venue: e.target.value }))}
              className="input-field text-sm"
            />
            <input
              type="number"
              placeholder="Min duration (min)"
              value={filters.minDuration}
              onChange={(e) => setFilters((f) => ({ ...f, minDuration: e.target.value }))}
              className="input-field text-sm"
            />
            <input
              type="number"
              placeholder="Max duration"
              value={filters.maxDuration}
              onChange={(e) => setFilters((f) => ({ ...f, maxDuration: e.target.value }))}
              className="input-field text-sm"
            />
            <input
              type="number"
              placeholder="Min cash in"
              value={filters.minCashIn}
              onChange={(e) => setFilters((f) => ({ ...f, minCashIn: e.target.value }))}
              className="input-field text-sm"
            />
            <input
              type="number"
              placeholder="Max cash in"
              value={filters.maxCashIn}
              onChange={(e) => setFilters((f) => ({ ...f, maxCashIn: e.target.value }))}
              className="input-field text-sm"
            />
            <input
              placeholder="EGM"
              value={filters.egm}
              onChange={(e) => setFilters((f) => ({ ...f, egm: e.target.value }))}
              className="input-field text-sm"
            />
          </div>
          <div className="flex justify-between items-center mt-3">
            <button type="button" onClick={clearFilters} className="text-xs text-psg-teal hover:underline">Clear filters</button>
            <span className="text-xs text-gray-500">
              {filtered.length} session(s) · Cash In: ${totalCashIn.toLocaleString()} · Turnover: ${totalTurnover.toLocaleString()}
            </span>
          </div>
        </div>
        <div className="flex-1 overflow-auto p-4 min-h-0">
          {loading && <div className="py-16 text-center text-gray-500">Loading gaming sessions…</div>}
          {!loading && loadError && (
            <div className="py-16 text-center text-red-600 text-sm">{loadError}</div>
          )}
          {!loading && !loadError && (
            <>
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 sticky top-0 z-10">
                  <tr>
                    <th className="px-4 py-3 text-left font-medium text-gray-600">Date</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-600">Venue</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-600">Duration (min)</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-600">Cash In</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-600">Turnover</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-600">EGM</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((r) => (
                    <tr key={r.id} className="border-b hover:bg-gray-50">
                      <td className="px-4 py-3">{r.date}</td>
                      <td className="px-4 py-3">{r.venue}</td>
                      <td className="px-4 py-3">{r.duration}</td>
                      <td className="px-4 py-3 font-mono">${r.cashIn.toLocaleString()}</td>
                      <td className="px-4 py-3 font-mono">${r.turnover.toLocaleString()}</td>
                      <td className="px-4 py-3 font-mono">{r.egm}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {filtered.length === 0 && (
                <div className="py-16 text-center text-gray-500">
                  {rows.length === 0 ? 'No gaming sessions in the last 12 months for this member.' : 'No sessions match filters'}
                </div>
              )}
            </>
          )}
        </div>
        <div className="p-4 border-t flex justify-between items-center bg-gray-50">
          <span className="text-sm text-gray-600">{filtered.length} row(s) shown</span>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={onClose}>Close</Button>
            <Button size="sm" onClick={handleExport} disabled={exporting || filtered.length === 0}>
              <DocumentArrowDownIcon className="w-4 h-4 mr-1" />
              {exporting ? 'Exporting…' : 'Export to CSV & Attach'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}

function ChecklistItem({ item, onStatusChange, onFindingsChange, onAttachDocuments, memberId, memberName, onShowPayoutHistory, onShowGamingSessions, caseId, readOnly = false }) {
  const [expanded, setExpanded] = useState(item.status === 'in_progress')
  const [findingsDraft, setFindingsDraft] = useState(item.findings || '')
  const [uploading, setUploading] = useState(false)
  const st = ITEM_STATUS[item.status] || ITEM_STATUS.not_started
  const fileInputRef = useRef(null)
  const kycFileInputRef = useRef(null)

  useEffect(() => {
    setFindingsDraft(item.findings || '')
  }, [item.findings])

  const handleFilesSelected = async (e) => {
    const files = e.target.files
    if (!files?.length || !caseId) return
    
    setUploading(true)
    try {
      const uploadedNames = []
      for (const file of Array.from(files)) {
        const fd = new FormData()
        fd.append('file', file)
        
        const res = await authFetch(
          getLocalApiUrl(`/api/cases/${encodeURIComponent(caseId)}/forms/ecdd/documents`),
          { method: 'POST', body: fd }
        )
        
        if (res.ok) {
          uploadedNames.push(file.name)
        }
      }
      
      if (uploadedNames.length > 0 && onAttachDocuments) {
        onAttachDocuments(item.id, uploadedNames)
      }
    } catch (err) {
      console.error('Upload failed:', err)
      alert('Failed to upload one or more files')
    } finally {
      setUploading(false)
      e.target.value = ''
    }
  }

  return (
    <div
      className="rounded-lg mb-2 overflow-hidden border"
      style={{
        borderColor: item.mandatory && !isMandatoryResolved(item.status) ? '#fecaca' : '#f1f5f9',
        background: item.status === 'done' ? '#fafffe' : '#fff',
      }}
    >
      <div onClick={() => setExpanded(!expanded)} className="flex items-center gap-3 p-3 cursor-pointer hover:bg-gray-50/50">
        <div
          className="w-5 h-5 rounded-full flex-shrink-0 flex items-center justify-center text-xs font-bold"
          style={{
            border: `2px solid ${st.ring}`,
            background: item.status === 'done' ? '#16a34a' : 'transparent',
            color: '#fff',
          }}
        >
          {item.status === 'done' && '✓'}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex gap-2 items-center flex-wrap">
            <span className={`text-sm font-semibold ${item.status === 'done' ? 'text-green-600' : 'text-psg-teal'}`}>
              {item.label}
            </span>
            {item.mandatory && <Badge bg="#fef2f2" color="#dc2626" border="#fecaca">Required</Badge>}
          </div>
          {item.assignedTo && (
            <span className="text-xs text-gray-500">
              Assigned: {item.assignedTo}
              {item.completedDate && ` • Completed: ${item.completedDate}`}
            </span>
          )}
        </div>
        <div
          className="relative flex-shrink-0 flex items-center"
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <select
            value={item.status}
            onChange={(e) => onStatusChange(item.id, e.target.value)}
            disabled={readOnly}
            aria-label={`Status: ${item.label}`}
            className="appearance-none text-xs font-semibold rounded-full border pl-2.5 pr-7 py-1 cursor-pointer max-w-[9.5rem] truncate disabled:opacity-60 disabled:cursor-not-allowed"
            style={{ color: st.color, backgroundColor: st.bg, borderColor: st.ring }}
          >
            {['not_started', 'in_progress', 'done', 'na'].map((s) => (
              <option key={s} value={s}>
                {ITEM_STATUS[s].label}
              </option>
            ))}
          </select>
          <ChevronDownIcon className="w-3.5 h-3.5 text-gray-500 absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none" />
        </div>
        {(item.docs?.length > 0 || item.kycDocs?.length > 0) && <PaperClipIcon className="w-4 h-4 text-gray-400" />}
        <ChevronRightIcon className={`w-4 h-4 text-gray-400 transition-transform ${expanded ? 'rotate-90' : ''}`} />
      </div>
      {expanded && (
        <div className="px-4 pb-4 pt-0 ml-8 border-t border-gray-50">
          <div className="p-3 bg-gray-50 rounded-lg mt-3 mb-3 border border-gray-100">
            <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Guidance</div>
            <div className="text-sm text-gray-500 leading-relaxed">{item.guidance}</div>
          </div>
          <div className="mb-3">
            <label className="block text-sm font-semibold text-gray-600 mb-1.5">Findings / Notes</label>
            <textarea
              value={findingsDraft}
              readOnly={readOnly || !onFindingsChange}
              onChange={(e) => {
                const v = e.target.value
                setFindingsDraft(v)
                if (!readOnly && onFindingsChange) onFindingsChange(item.id, v)
              }}
              onBlur={() => {
                if (!readOnly && onFindingsChange && findingsDraft !== (item.findings || '')) {
                  onFindingsChange(item.id, findingsDraft)
                }
              }}
              placeholder="Record what was found, observed, or confirmed..."
              className="w-full min-h-[140px] p-3 rounded-lg border border-gray-200 text-sm text-gray-700 bg-white resize-y outline-none focus:border-gray-300 read-only:bg-gray-50"
            />
          </div>
          {/* System data: View from platform (digital-first) */}
          {(item.dataSource === 'payout_history' || item.dataSource === 'gaming_session') && (
            <div className="mb-3">
              <label className="block text-sm font-semibold text-gray-600 mb-1.5">Data from system</label>
              <p className="text-xs text-gray-500 mb-2">View data directly from the platform. Filter, export to XLS and attach if needed.</p>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  if (item.dataSource === 'payout_history') onShowPayoutHistory?.()
                  else onShowGamingSessions?.()
                }}
              >
                <EyeIcon className="w-3 h-3 mr-1" />
                {item.dataSource === 'payout_history' ? 'View Payout History' : 'View Gaming Sessions'}
              </Button>
            </div>
          )}
          {/* KYC: Documents collected + Submit for KYC */}
          {item.isKyc && (
            <div className="mb-3">
              <label className="block text-sm font-semibold text-gray-600 mb-1.5">Documents collected</label>
              {item.kycDocs?.length > 0 ? (
                <div className="flex gap-2 flex-wrap mb-2">
                  {item.kycDocs.map((d, i) => (
                    <span key={i} className="flex gap-1.5 items-center px-2.5 py-1.5 rounded-md bg-gray-100 text-sm text-gray-600">
                      <PaperClipIcon className="w-3 h-3" />{d}
                    </span>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-gray-500 mb-2">No documents collected yet.</p>
              )}
              <input
                ref={kycFileInputRef}
                type="file"
                className="hidden"
                multiple
                onChange={handleFilesSelected}
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="text-psg-teal border-psg-teal hover:bg-psg-teal/5"
                onClick={(e) => {
                  e.stopPropagation()
                  kycFileInputRef.current?.click()
                }}
                disabled={uploading}
              >
                <ArrowUpTrayIcon className="w-3 h-3 mr-1" /> {uploading ? 'Uploading...' : 'Upload Document'}
              </Button>
              <Button size="sm" className="ml-2 bg-psg-teal hover:bg-psg-teal-700">
                <ShieldCheckIcon className="w-3 h-3 mr-1" /> Submit for KYC
              </Button>
            </div>
          )}
          {/* Attachments (for SoF and other upload-only items) */}
          {!item.dataSource && !item.isKyc && (
            <div>
              <label className="block text-sm font-semibold text-gray-600 mb-1.5">Attachments</label>
              {item.docs?.length > 0 && (
                <div className="flex gap-2 flex-wrap mb-2">
                  {item.docs.map((d, i) => (
                    <span key={i} className="flex gap-1.5 items-center px-2.5 py-1.5 rounded-md bg-gray-100 text-sm text-gray-500">
                      <PaperClipIcon className="w-3 h-3" />{d}
                    </span>
                  ))}
                </div>
              )}
              <input
                ref={fileInputRef}
                type="file"
                className="hidden"
                multiple
                onChange={handleFilesSelected}
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation()
                  fileInputRef.current?.click()
                }}
                disabled={uploading}
              >
                <ArrowUpTrayIcon className="w-3 h-3 mr-1" /> {uploading ? 'Uploading...' : 'Upload Document'}
              </Button>
            </div>
          )}
          {/* Attachments for dataSource items (exported file) */}
          {item.dataSource && item.docs?.length > 0 && (
            <div className="mt-2">
              <label className="block text-sm font-semibold text-gray-600 mb-1.5">Attached (exported)</label>
              <div className="flex gap-2 flex-wrap">
                {item.docs.map((d, i) => (
                  <span key={i} className="flex gap-1.5 items-center px-2.5 py-1.5 rounded-md bg-gray-100 text-sm text-gray-500">
                    <PaperClipIcon className="w-3 h-3" />{d}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

function BucketSection({ bucket, onStatusChange, onFindingsChange, onAttachDocuments, memberId, memberName, onShowPayoutHistory, onShowGamingSessions, caseId, readOnly = false }) {
  const [collapsed, setCollapsed] = useState(() => bucket.id !== 'custom')
  const done = bucket.items.filter((i) => i.status === 'done' || i.status === 'na').length
  const total = bucket.items.length
  const mandatoryRemaining = bucket.items.filter((i) => i.mandatory && !isMandatoryResolved(i.status)).length
  const isComplete = total > 0 && done === total

  return (
    <Card className="mb-3">
      <div onClick={() => setCollapsed(!collapsed)} className="flex items-center gap-3 px-4 py-3.5 cursor-pointer hover:bg-gray-50/50 transition-colors rounded-lg">
        <div
          className="w-6 h-6 rounded-full flex-shrink-0 flex items-center justify-center"
          style={{
            backgroundColor: isComplete ? '#f0fdf4' : '#f8fafc',
            border: `2px solid ${isComplete ? '#16a34a' : '#e5e7eb'}`,
            color: isComplete ? '#16a34a' : '#6B7280',
          }}
        >
          {isComplete ? <CheckCircleIcon className="w-4 h-4" /> : <span className="w-2 h-2 rounded-full bg-gray-300" />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex gap-2 items-center flex-wrap">
            <span className="text-base font-semibold text-psg-teal">{bucket.title}</span>
            <span className={`text-sm font-mono ${isComplete ? 'text-green-600' : 'text-gray-500'}`}>
              {done}/{total}
            </span>
            {mandatoryRemaining > 0 && (
              <Badge bg="#fef2f2" color="#dc2626" border="#fecaca">{mandatoryRemaining} required</Badge>
            )}
          </div>
          <div className="text-sm text-gray-500 mt-0.5 truncate">{bucket.description}</div>
        </div>
        <ChevronRightIcon className={`w-4 h-4 text-gray-400 flex-shrink-0 transition-transform ${collapsed ? '' : 'rotate-90'}`} />
      </div>
      {!collapsed && (
        <CardContent className="px-4 pb-4 pt-1 border-t border-gray-50">
          {bucket.items.map((item) => (
            <ChecklistItem
              key={item.id}
              item={item}
              onStatusChange={onStatusChange}
              onFindingsChange={onFindingsChange}
              onAttachDocuments={onAttachDocuments}
              memberId={memberId}
              memberName={memberName}
              onShowPayoutHistory={onShowPayoutHistory}
              onShowGamingSessions={onShowGamingSessions}
              caseId={caseId}
              readOnly={readOnly}
            />
          ))}
        </CardContent>
      )}
    </Card>
  )
}

export default function ECDDDetail() {
  const { id } = useParams()
  const { can } = useAuth()
  const { toolEnabled } = usePatronScreeningToolEnabled()
  const [searchParams] = useSearchParams()
  const memberNameFromUrl = searchParams.get('memberName') || ''
  const memberIdFromUrl = searchParams.get('memberId') || ''
  const [buckets, setBuckets] = useState(INITIAL_BUCKETS)
  const [selectedDecision, setSelectedDecision] = useState(null)
  const [decisionRationale, setDecisionRationale] = useState('')
  const [submittingDecision, setSubmittingDecision] = useState(false)
  const [submitDecisionError, setSubmitDecisionError] = useState('')
  const [noteDraft, setNoteDraft] = useState('')
  const [notesSaving, setNotesSaving] = useState(false)
  const [noteError, setNoteError] = useState('')
  const [baseRecord, setBaseRecord] = useState(null)
  const [loading, setLoading] = useState(true)
  const [fetchError, setFetchError] = useState(null)
  const [payoutModalOpen, setPayoutModalOpen] = useState(false)
  const [gamingSessionModalOpen, setGamingSessionModalOpen] = useState(false)
  const [customItemModalOpen, setCustomItemModalOpen] = useState(false)
  const [patronScreeningToolOpen, setPatronScreeningToolOpen] = useState(false)
  const [searchAuditRefreshKey, setSearchAuditRefreshKey] = useState(0)
  const [approvingEcdd, setApprovingEcdd] = useState(false)
  const [returningEcdd, setReturningEcdd] = useState(false)
  const [approvalError, setApprovalError] = useState('')
  const [returnReason, setReturnReason] = useState('')
  const [showReturnModal, setShowReturnModal] = useState(false)
  const [closingEcdd, setClosingEcdd] = useState(false)
  const [canApproveEcdd, setCanApproveEcdd] = useState(false)
  const [checkingApprovalPermission, setCheckingApprovalPermission] = useState(true)
  const [helpPanelOpen, setHelpPanelOpen] = useState(false)
  /** Latest checklist draft — avoids losing in-progress findings when submit is clicked before textarea blur. */
  const bucketsRef = useRef(INITIAL_BUCKETS)
  const persistDebounceRef = useRef(null)

  useEffect(() => {
    bucketsRef.current = buckets
  }, [buckets])

  useEffect(() => {
    return () => {
      if (persistDebounceRef.current) clearTimeout(persistDebounceRef.current)
    }
  }, [])

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      setLoading(true)
      setFetchError(null)
      try {
        const r = await authFetch(getLocalApiUrl(`/api/ecdd/${encodeURIComponent(id)}`))
        const j = await r.json()
        if (cancelled) return
        if (!j.success) throw new Error(j.error || 'Failed to load ECDD')
        setBaseRecord(j.data)
        if (!j.data?.linkedCase) {
          const hydrated = hydrateEcddDraftFromRecord(j.data)
          if (hydrated) {
            setBuckets(hydrated.buckets)
            if (hydrated.decision) setSelectedDecision(hydrated.decision)
            if (hydrated.rationale) setDecisionRationale(hydrated.rationale)
          }
        }
      } catch (e) {
        if (!cancelled) {
          setFetchError(e.message || 'Failed to load ECDD')
          setBaseRecord(null)
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => { cancelled = true }
  }, [id])

  const navigate = useNavigate()
  const linkedCaseId = baseRecord?.linkedCase || null
  const isPendingApproval = baseRecord?.status === 'pending_approval'
  const isApprovedMonitoring = baseRecord?.status === 'approved_monitoring'
  const isApprovedSmr = baseRecord?.status === 'approved_smr'
  const isClosed = baseRecord?.status === 'closed'
  const isApproved = isApprovedMonitoring || isApprovedSmr

  // Check if current user can approve ECDDs
  useEffect(() => {
    let cancelled = false
    ;(async () => {
      setCheckingApprovalPermission(true)
      try {
        const r = await authFetch(getLocalApiUrl('/api/ecdd-approval-triggers/can-approve'))
        const j = await r.json()
        if (cancelled) return
        if (j.success && j.data) {
          setCanApproveEcdd(j.data.can_approve === true)
        }
      } catch (e) {
        // On error, default to not allowing approval
        if (!cancelled) setCanApproveEcdd(false)
      } finally {
        if (!cancelled) setCheckingApprovalPermission(false)
      }
    })()
    return () => { cancelled = true }
  }, [])

  const snapshotKey = baseRecord
    ? `${JSON.stringify(baseRecord.bucketsSnapshot ?? null)}|${baseRecord.resolution || ''}`
    : ''

  useEffect(() => {
    if (!baseRecord) return
    let cancelled = false
    ;(async () => {
      try {
        if (linkedCaseId) {
          const r = await authFetch(
            getLocalApiUrl(`/api/cases/${encodeURIComponent(linkedCaseId)}/forms/ecdd`)
          )
          const j = await r.json()
          if (cancelled || !j.success || !j.data) return
          const data = typeof j.data === 'string' ? JSON.parse(j.data) : j.data
          if (data.buckets?.length) setBuckets(data.buckets)
          if (data.decision) setSelectedDecision(data.decision)
          if (data.decisionRationale) setDecisionRationale(data.decisionRationale)
          return
        }
        const hydrated = hydrateEcddDraftFromRecord(baseRecord)
        if (cancelled || !hydrated) return
        setBuckets(hydrated.buckets)
        if (hydrated.decision) setSelectedDecision(hydrated.decision)
        if (hydrated.rationale) setDecisionRationale(hydrated.rationale)
      } catch {
        /* keep INITIAL_BUCKETS */
      }
    })()
    return () => { cancelled = true }
  }, [baseRecord?.id, snapshotKey, linkedCaseId])

  if (loading) {
    return (
      <div className="space-y-6">
        <Link to="/compliance-safe/ecdd" className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-600">
          <ChevronLeftIcon className="w-4 h-4" /> Back to ECDD List
        </Link>
        <div className="py-12 text-center text-gray-500">Loading…</div>
      </div>
    )
  }

  if (fetchError || !baseRecord) {
    return (
      <div className="space-y-6">
        <Link to="/compliance-safe/ecdd" className="inline-flex items-center gap-2 text-sm font-medium text-psg-teal hover:underline">
          <ChevronLeftIcon className="w-4 h-4" /> Back to ECDD List
        </Link>
        <Card className="border-red-200 bg-red-50">
          <CardContent className="p-6 text-center">
            <ExclamationTriangleIcon className="mx-auto mb-2 w-8 h-8 text-red-500" />
            <h2 className="text-lg font-semibold text-red-700">ECDD Record Not Found</h2>
            <p className="text-sm text-red-600 mt-1">{fetchError || `No record found with ID: ${id}`}</p>
          </CardContent>
        </Card>
      </div>
    )
  }

  // When linked from Case Dashboard, use the case's member name for consistency
  const patronIdentity = resolvePatronIdentity(baseRecord)
  const displayName = (memberNameFromUrl && memberIdFromUrl && String(patronIdentity.member_id || baseRecord.member_id || '') === String(memberIdFromUrl))
    ? memberNameFromUrl
    : (baseRecord.name || baseRecord.member_name || '')
  const profileLinkId =
    patronIdentity.master_profile_id ||
    patronIdentity.member_id ||
    patronIdentity.visitor_id ||
    baseRecord.member_id ||
    ''
  const gamingMemberId = patronIdentity.member_id || baseRecord.member_id || ''
  const record = {
    ...baseRecord,
    name: displayName,
    pid: patronIdentity.patron_id || baseRecord.pid || baseRecord.member_id,
    patron_id: patronIdentity.patron_id,
    profileLinkId,
    gamingMemberId,
    kycStatus: 'Verified',
    pepStatus: baseRecord.triggerType === 'pep' ? 'Foreign PEP — Former Deputy Minister of Finance, Greece (2018–2022)' : null,
    triggerDetail: {
      type: baseRecord.triggerType,
      label: baseRecord.trigger,
      description: baseRecord.triggerType === 'pep' ? 'PEP screening during KYC pipeline returned positive match.' : `Investigation triggered by: ${baseRecord.trigger}`,
      source: baseRecord.linkedAlert ? `Alert — ${baseRecord.linkedAlert}` : 'Manual observation',
      linkedAlert: baseRecord.linkedAlert,
      date: baseRecord.triggerDate,
    },
    participants: [`${baseRecord.assignedTo} (CO)`, baseRecord.manager ? `${baseRecord.manager} (Manager)` : null, 'Floor Staff'].filter(Boolean),
  }

  const queuePersistDraft = (nextBuckets, decision, rationale) => {
    if (baseRecord?.status !== 'open') return
    if (persistDebounceRef.current) clearTimeout(persistDebounceRef.current)
    persistDebounceRef.current = setTimeout(() => {
      persistEcddDraft(nextBuckets, decision, rationale).catch(() => {})
    }, 500)
  }

  const handleStatusChange = (itemId, newStatus) => {
    setBuckets((prev) => {
      const updated = prev.map((b) => ({
        ...b,
        items: b.items.map((i) => (i.id === itemId ? { ...i, status: newStatus } : i)),
      }))
      bucketsRef.current = updated
      if (baseRecord?.status === 'open') {
        if (persistDebounceRef.current) clearTimeout(persistDebounceRef.current)
        persistEcddDraft(updated, selectedDecision, decisionRationale).catch(() => {})
      }
      return updated
    })
  }

  const handleFindingsChange = (itemId, findings) => {
    setBuckets((prev) => {
      const updated = prev.map((b) => ({
        ...b,
        items: b.items.map((i) => (i.id === itemId ? { ...i, findings } : i)),
      }))
      bucketsRef.current = updated
      queuePersistDraft(updated, selectedDecision, decisionRationale)
      return updated
    })
  }

  const handleAttachDocuments = (itemId, fileNames) => {
    if (!fileNames?.length) return
    setBuckets((prev) => {
      const updated = prev.map((b) => ({
        ...b,
        items: b.items.map((i) => {
          if (i.id !== itemId) return i
          if (i.isKyc) {
            return { ...i, kycDocs: [...(i.kycDocs || []), ...fileNames] }
          }
          return { ...i, docs: [...(i.docs || []), ...fileNames] }
        }),
      }))
      bucketsRef.current = updated
      if (baseRecord?.status === 'open') {
        if (persistDebounceRef.current) clearTimeout(persistDebounceRef.current)
        persistEcddDraft(updated, selectedDecision, decisionRationale).catch(() => {})
      }
      return updated
    })
  }

  const handleAddEcddNote = async () => {
    const text = noteDraft.trim()
    if (!text || notesSaving) return
    setNotesSaving(true)
    setNoteError('')
    try {
      const r = await authFetch(getLocalApiUrl(`/api/ecdd/${encodeURIComponent(id)}/notes`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text }),
      })
      const j = await r.json().catch(() => ({}))
      if (!r.ok || !j.success) throw new Error(j.error || 'Failed to save note')
      setBaseRecord((prev) => (prev ? { ...prev, ...j.data } : prev))
      setNoteDraft('')
    } catch (e) {
      setNoteError(e.message || 'Failed to save note')
    } finally {
      setNotesSaving(false)
    }
  }

  const persistEcddDraft = async (nextBuckets, decision, rationale) => {
    const payload = {
      buckets: nextBuckets ?? buckets,
      decision: decision ?? selectedDecision,
      decisionRationale: (rationale ?? decisionRationale).trim(),
    }
    if (linkedCaseId) {
      const r = await authFetch(
        getLocalApiUrl(`/api/cases/${encodeURIComponent(linkedCaseId)}/forms/ecdd`),
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ data: payload }),
        }
      )
      const j = await r.json().catch(() => ({}))
      if (!r.ok || j.success === false) throw new Error(j.error || 'Failed to save ECDD draft')
      return
    }
    const currentBuckets = nextBuckets ?? buckets
    const items = currentBuckets.flatMap((b) => b.items)
    const mandatory = items.filter((i) => i.mandatory)
    const r = await authFetch(getLocalApiUrl(`/api/ecdd/${encodeURIComponent(id)}`), {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        checklist_done: items.filter((i) => i.status === 'done' || i.status === 'na').length,
        checklist_mandatory_done: mandatory.filter((i) => isMandatoryResolved(i.status)).length,
        buckets: currentBuckets,
        decision: decision ?? selectedDecision,
        decisionRationale: (rationale ?? decisionRationale).trim(),
      }),
    })
    const j = await r.json().catch(() => ({}))
    if (!r.ok || j.success === false) {
      throw new Error(j.error || 'Failed to save ECDD draft')
    }
    if (j.data?.bucketsSnapshot) {
      setBaseRecord((prev) => (prev ? { ...prev, ...j.data } : prev))
    }
  }

  const handleSubmitForApproval = async () => {
    if (!selectedDecision || !decisionRationale.trim() || submittingDecision || isPendingApproval || isClosed) return
    setSubmitDecisionError('')
    setSubmittingDecision(true)
    try {
      if (persistDebounceRef.current) {
        clearTimeout(persistDebounceRef.current)
        persistDebounceRef.current = null
      }
      const draftBuckets = bucketsRef.current
      const payload = {
        buckets: draftBuckets,
        decision: selectedDecision,
        decisionRationale: decisionRationale.trim(),
      }
      await persistEcddDraft(draftBuckets, selectedDecision, decisionRationale.trim())

      // If linked to a case, ensure ECDD record exists before submitting
      if (linkedCaseId) {
        const createRes = await authFetch(
          getLocalApiUrl(`/api/cases/${encodeURIComponent(linkedCaseId)}/create-ecdd`),
          { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' }
        )
        const createJson = await createRes.json().catch(() => ({}))
        if (!createRes.ok || !createJson.success) {
          throw new Error(createJson.error || 'Failed to create ECDD record')
        }
      }

      const submitUrl = linkedCaseId
        ? getLocalApiUrl(`/api/cases/${encodeURIComponent(linkedCaseId)}/forms/ecdd/submit`)
        : getLocalApiUrl(`/api/ecdd/${encodeURIComponent(id)}/submit-for-approval`)
      const r = await authFetch(submitUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(linkedCaseId ? { data: payload } : payload),
      })
      const j = await r.json().catch(() => ({}))
      if (!r.ok || !j.success) {
        throw new Error(j.errors?.[0]?.message || j.error || 'Submit failed')
      }
      const merged = { ...(j.data || {}), status: 'pending_approval' }
      setBaseRecord((prev) => (prev ? { ...prev, ...merged } : prev))
      const hydrated = hydrateEcddDraftFromRecord(merged)
      if (hydrated) {
        setBuckets(hydrated.buckets)
        if (hydrated.decision) setSelectedDecision(hydrated.decision)
        if (hydrated.rationale) setDecisionRationale(hydrated.rationale)
      }
    } catch (e) {
      setSubmitDecisionError(e.message || 'Failed to submit for manager approval')
    } finally {
      setSubmittingDecision(false)
    }
  }

  const handleApproveEcdd = async () => {
    if (approvingEcdd) return
    setApprovalError('')
    setApprovingEcdd(true)
    try {
      const r = await authFetch(getLocalApiUrl(`/api/ecdd/${encodeURIComponent(id)}/approve`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      })
      const j = await r.json().catch(() => ({}))
      if (!r.ok || !j.success) {
        throw new Error(j.error || 'Approval failed')
      }
      // Update status based on the decision outcome
      setBaseRecord((prev) => (prev ? { ...prev, ...j.data, status: j.newStatus || j.data?.status } : prev))
      
      // Show success message based on decision
      if (j.message) {
        alert(j.message)
      }
    } catch (e) {
      setApprovalError(e.message || 'Failed to approve ECDD')
    } finally {
      setApprovingEcdd(false)
    }
  }

  const handleReturnEcdd = async () => {
    if (returningEcdd || !returnReason.trim()) return
    setApprovalError('')
    setReturningEcdd(true)
    try {
      const r = await authFetch(getLocalApiUrl(`/api/ecdd/${encodeURIComponent(id)}/return`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ returnReason: returnReason.trim() }),
      })
      const j = await r.json().catch(() => ({}))
      if (!r.ok || !j.success) {
        throw new Error(j.error || 'Return failed')
      }
      setBaseRecord((prev) => (prev ? { ...prev, ...j.data, status: 'open' } : prev))
      setShowReturnModal(false)
      setReturnReason('')
    } catch (e) {
      setApprovalError(e.message || 'Failed to return ECDD')
    } finally {
      setReturningEcdd(false)
    }
  }

  const handleCloseEcdd = async (smrReference = '') => {
    if (closingEcdd) return
    setApprovalError('')
    setClosingEcdd(true)
    try {
      const r = await authFetch(getLocalApiUrl(`/api/ecdd/${encodeURIComponent(id)}/close`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ smrReference }),
      })
      const j = await r.json().catch(() => ({}))
      if (!r.ok || !j.success) {
        throw new Error(j.error || 'Close failed')
      }
      setBaseRecord((prev) => (prev ? { ...prev, ...j.data, status: 'closed' } : prev))
      if (j.message) {
        alert(j.message)
      }
    } catch (e) {
      setApprovalError(e.message || 'Failed to close ECDD')
    } finally {
      setClosingEcdd(false)
    }
  }

  const handleAddCustomItem = ({ label, guidance, mandatory }) => {
    const rowId = `custom_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`
    const newItem = {
      id: rowId,
      label,
      guidance: guidance || 'No additional guidance.',
      mandatory: !!mandatory,
      status: 'not_started',
      assignedTo: null,
      completedDate: null,
      findings: '',
      docs: [],
      dataSource: null,
      custom: true,
    }
    setBuckets((prev) => {
      const updated = prev.map((b) => (b.id === 'custom' ? { ...b, items: [...b.items, newItem] } : b))
      bucketsRef.current = updated
      if (baseRecord?.status === 'open') {
        persistEcddDraft(updated, selectedDecision, decisionRationale).catch(() => {})
      }
      return updated
    })
  }

  const allItems = buckets.flatMap((b) => b.items)
  const totalItems = allItems.length
  const doneItems = allItems.filter((i) => i.status === 'done' || i.status === 'na').length
  const mandatoryItems = allItems.filter((i) => i.mandatory)
  const mandatoryDone = mandatoryItems.filter((i) => isMandatoryResolved(i.status)).length
  const allMandatoryDone = mandatoryDone === mandatoryItems.length
  const pct = totalItems > 0 ? Math.round((doneItems / totalItems) * 100) : 0
  const riskColor = record.risk >= 70 ? '#dc2626' : record.risk >= 40 ? '#ea580c' : '#16a34a'
  const ecddNotes = normalizeEcddNotes(baseRecord.notesLog)
  const ecddNotesNewestFirst = [...ecddNotes].reverse()

  return (
    <div className="space-y-6">
      <Link to="/compliance-safe/ecdd" className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-600 transition-colors">
        <ChevronLeftIcon className="w-4 h-4" /> Back to ECDD List
      </Link>

      <div className="flex justify-end">
        <HelpScreenButton
          screenKey={ECDD_DETAIL_HELP_SCREEN_KEY}
          onClick={() => setHelpPanelOpen(true)}
        />
      </div>

      {/* Header — member identity + primary actions only (status / risk / team live in the right details column) */}
      <Card>
        <CardContent className="p-5">
          <div className="flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-start">
            <div className="min-w-0">
              <h2 className="text-xl font-bold text-psg-teal m-0">{record.name}</h2>
              <div className="text-xs font-mono text-gray-500 mt-1.5">{record.id}</div>
              <div className="mt-1.5">
                <PatronIdBadge
                  patronId={patronIdentity.master_profile_id || patronIdentity.patron_id}
                  memberId={patronIdentity.member_id}
                  visitorId={patronIdentity.visitor_id}
                  profileType={patronIdentity.subject_type === 'member' ? 'MEMBER' : patronIdentity.subject_type === 'visitor' ? 'VISITOR' : null}
                  variant="compact"
                />
              </div>
            </div>
            <div className="flex flex-wrap gap-2 shrink-0">
              <Link to={`/profile-safe/search?memberId=${encodeURIComponent(profileLinkId)}`}>
                <Button variant="outline" size="sm"><EyeIcon className="w-4 h-4 mr-1" /> View Profile</Button>
              </Link>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="text-red-600 border-red-300 hover:bg-red-50"
                onClick={() => {
                  if (linkedCaseId) {
                    navigate(
                      `/compliance-safe/investigations/${encodeURIComponent(linkedCaseId)}?tab=smr_form`
                    )
                    return
                  }
                  if (baseRecord?.linkedSmr) {
                    navigate('/compliance-safe/smr-registry')
                    return
                  }
                  window.alert(
                    'No investigation case is linked to this ECDD. Complete manager approval with Escalate → SMR, or start an investigation from the payout / alert workflow first.'
                  )
                }}
              >
                <ExclamationTriangleIcon className="w-4 h-4 mr-1" /> Create SMR Now
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid gap-4 lg:grid-cols-[1fr_320px]">
        {/* Left: Checklist */}
        <div>
          <div className="mb-4">
            <div className="flex justify-between items-center mb-3 gap-2 flex-wrap">
              <span className="text-base font-bold text-psg-teal">ECDD Checklist</span>
              <div className="flex flex-wrap gap-2 justify-end">
                {isPatronScreeningPrototypeEnabled() && can('compliance-safe/patron-screening', 'view') && toolEnabled && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="border-psg-teal/40 text-psg-teal hover:bg-psg-teal/5"
                  onClick={() => setPatronScreeningToolOpen(true)}
                  title="Open OSINT screening sources for this ECDD. Visibility is controlled in Patron Screening Settings."
                >
                  <MagnifyingGlassIcon className="w-4 h-4 mr-1" />
                  Screening sources
                </Button>
                )}
                <Button type="button" variant="outline" size="sm" onClick={() => setCustomItemModalOpen(true)}>
                  <PlusIcon className="w-4 h-4 mr-1" /> Add Custom Item
                </Button>
              </div>
            </div>
            {buckets.map((b) => (
              <BucketSection
                key={b.id}
                bucket={b}
                onStatusChange={handleStatusChange}
                onFindingsChange={isPendingApproval || isClosed || isApproved ? undefined : handleFindingsChange}
                onAttachDocuments={handleAttachDocuments}
                memberId={record.gamingMemberId}
                memberName={record.name}
                onShowPayoutHistory={() => setPayoutModalOpen(true)}
                onShowGamingSessions={() => setGamingSessionModalOpen(true)}
                caseId={linkedCaseId || undefined}
                readOnly={isPendingApproval || isClosed || isApproved}
              />
            ))}

            {isPatronScreeningPrototypeEnabled() && can('compliance-safe/patron-screening', 'view') && toolEnabled && (
              <SearchAuditPanel
                patronId={record.gamingMemberId || null}
                memberName={record.name}
                refreshKey={searchAuditRefreshKey}
                canOpenScreening
                onOpenScreening={() => setPatronScreeningToolOpen(true)}
              />
            )}
          </div>

          {/* General notes — timestamped entries, persisted via POST /api/ecdd/:id/notes */}
          <Card className="mt-4 mb-4 border border-gray-200">
            <CardContent className="p-4">
              <div className="flex justify-between items-start gap-2 mb-2">
                <div>
                  <span className="text-base font-bold text-psg-teal">General notes</span>
                  <p className="text-xs text-gray-500 mt-1">
                    Add dated entries for this ECDD. Each note is saved to the record with a timestamp.
                  </p>
                </div>
                <span className="text-xs text-gray-500 whitespace-nowrap">{ecddNotes.length} note{ecddNotes.length !== 1 ? 's' : ''}</span>
              </div>
              <div className="flex flex-col sm:flex-row gap-2 mb-3">
                <textarea
                  value={noteDraft}
                  onChange={(e) => setNoteDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                      e.preventDefault()
                      handleAddEcddNote()
                    }
                  }}
                  placeholder="Write a note… (Ctrl+Enter or ⌘+Enter to add)"
                  rows={4}
                  disabled={notesSaving}
                  className="flex-1 min-h-[100px] p-3 rounded-lg border border-gray-200 text-sm text-gray-800 bg-white resize-y outline-none focus:border-gray-300 leading-relaxed disabled:opacity-60"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="sm:self-end shrink-0 border-psg-teal text-psg-teal hover:bg-psg-teal/5"
                  disabled={notesSaving || !noteDraft.trim()}
                  onClick={() => handleAddEcddNote()}
                >
                  <ChatBubbleLeftRightIcon className="w-4 h-4 mr-1" />
                  {notesSaving ? 'Saving…' : 'Add note'}
                </Button>
              </div>
              {noteError && <p className="text-sm text-red-600 mb-2">{noteError}</p>}
              {ecddNotesNewestFirst.length === 0 ? (
                <p className="text-sm text-gray-400 py-2">No notes yet.</p>
              ) : (
                <ul className="border-t border-gray-100 pt-3 space-y-3">
                  {ecddNotesNewestFirst.map((n) => (
                    <li key={n.id} className="border-l-2 border-psg-teal/30 pl-3">
                      <div className="text-xs text-gray-500 font-mono mb-0.5">
                        {formatEcddNoteTs(n.ts)} · <span className="font-sans font-medium text-gray-600">{n.who}</span>
                      </div>
                      <div className="text-sm text-gray-800 whitespace-pre-wrap leading-relaxed">{n.text}</div>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>

          {/* Decision Section */}
          <Card className={`mt-4 ${isClosed ? 'border-green-200' : allMandatoryDone ? 'border-green-200' : 'opacity-60'}`}>
            <CardContent className="p-4">
              {isClosed ? (
                <>
                  <div className="flex gap-2 items-center mb-3">
                    <CheckCircleIcon className="w-5 h-5 text-green-600" />
                    <span className="text-base font-bold text-green-700">ECDD Closed</span>
                  </div>
                  <div className="rounded-lg border border-green-200 bg-green-50 px-4 py-3">
                    <div className="text-sm text-green-800">
                      This ECDD assessment has been closed with decision: <strong>{selectedDecision === 'satisfactory' ? 'Satisfactory' : selectedDecision === 'enhanced_monitoring' ? 'Enhanced Monitoring' : selectedDecision === 'escalated_smr' ? 'Escalate to SMR' : selectedDecision || 'N/A'}</strong>
                    </div>
                    {decisionRationale && (
                      <div className="mt-2 pt-2 border-t border-green-200">
                        <div className="text-xs font-semibold text-green-700 mb-1">Decision Rationale</div>
                        <div className="text-sm text-green-800 whitespace-pre-wrap">{decisionRationale}</div>
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <>
                  <div className="flex gap-2 items-center mb-3">
                    <ShieldCheckIcon className={`w-5 h-5 ${allMandatoryDone ? 'text-green-600' : 'text-gray-400'}`} />
                    <span className={`text-base font-bold ${allMandatoryDone ? 'text-psg-teal' : 'text-gray-500'}`}>Record Decision</span>
                    {!allMandatoryDone && <span className="text-sm text-red-600">Complete all required items first</span>}
                  </div>
                  {allMandatoryDone ? (
                    <>
                      <div className="mb-3">
                        <label className="block text-sm font-semibold text-gray-600 mb-2">Decision</label>
                        <div className="flex gap-2">
                          {[
                            { key: 'satisfactory', label: 'Close — Satisfactory', desc: 'No further action required', color: '#16a34a' },
                            { key: 'enhanced_monitoring', label: 'Enhanced Monitoring', desc: 'Keep under periodic review', color: '#ca8a04' },
                            { key: 'escalated_smr', label: 'Escalate → SMR', desc: 'File suspicious matter report', color: '#dc2626' },
                          ].map((d) => (
                            <div
                              key={d.key}
                              onClick={() => !isPendingApproval && !isApproved && setSelectedDecision(d.key)}
                              className={`flex-1 p-3 rounded-lg text-center transition-colors border-2 ${isPendingApproval || isApproved ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}
                              style={{
                                background: selectedDecision === d.key ? `${d.color}10` : '#fafbfc',
                                borderColor: selectedDecision === d.key ? d.color : '#f1f5f9',
                              }}
                            >
                              <div className="text-sm font-bold" style={{ color: d.color }}>{d.label}</div>
                              <div className="text-xs text-gray-500 mt-1">{d.desc}</div>
                            </div>
                          ))}
                        </div>
                      </div>
                      {selectedDecision && (
                        <div className="mb-3">
                          <label className="block text-sm font-semibold text-gray-600 mb-1.5">Decision Rationale</label>
                          <textarea
                            value={decisionRationale}
                            onChange={(e) => setDecisionRationale(e.target.value)}
                            placeholder="Document your reasoning for this decision..."
                            disabled={isPendingApproval || isApproved}
                            className="w-full min-h-[80px] p-3 rounded-lg border border-gray-200 text-sm text-gray-700 bg-white resize-y outline-none focus:border-gray-300 disabled:bg-gray-50"
                          />
                        </div>
                      )}
                      {isPendingApproval ? (
                        <div className="space-y-3">
                          <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
                            <p className="font-medium mb-1">This ECDD is awaiting senior management approval.</p>
                            <p className="text-xs">
                              Decision: <strong>{selectedDecision === 'satisfactory' ? 'Close — Satisfactory' : selectedDecision === 'enhanced_monitoring' ? 'Enhanced Monitoring' : selectedDecision === 'escalated_smr' ? 'Escalate to SMR' : selectedDecision || 'N/A'}</strong>
                            </p>
                          </div>
                          {checkingApprovalPermission ? (
                            <div className="text-sm text-gray-500">Checking approval permissions...</div>
                          ) : canApproveEcdd ? (
                            <>
                              <div className="flex gap-2">
                                <Button
                                  type="button"
                                  className="bg-green-600 hover:bg-green-700 text-white"
                                  disabled={approvingEcdd || returningEcdd}
                                  onClick={handleApproveEcdd}
                                >
                                  <CheckCircleIcon className="w-4 h-4 mr-1" />
                                  {approvingEcdd ? 'Approving…' : 'Approve Decision'}
                                </Button>
                                <Button
                                  type="button"
                                  variant="outline"
                                  className="border-amber-500 text-amber-700 hover:bg-amber-50"
                                  disabled={approvingEcdd || returningEcdd}
                                  onClick={() => setShowReturnModal(true)}
                                >
                                  <ArrowUturnLeftIcon className="w-4 h-4 mr-1" />
                                  Return for Revision
                                </Button>
                              </div>
                              <p className="text-xs text-gray-500">
                                {selectedDecision === 'satisfactory' && 'Approving will close this ECDD with no further action required.'}
                                {selectedDecision === 'enhanced_monitoring' && 'Approving will authorize adding this patron to the watchlist for periodic review.'}
                                {selectedDecision === 'escalated_smr' && 'Approving will authorize the compliance officer to file a Suspicious Matter Report.'}
                              </p>
                            </>
                          ) : (
                            <div className="text-sm text-gray-600 italic">
                              You do not have permission to approve this ECDD. Only designated approvers can approve ECDD decisions.
                            </div>
                          )}
                          {approvalError && (
                            <p className="text-sm text-red-600">{approvalError}</p>
                          )}
                          {/* Return Modal */}
                          {showReturnModal && (
                            <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
                              <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md">
                                <h3 className="text-lg font-bold text-gray-900 mb-3">Return ECDD for Revision</h3>
                                <p className="text-sm text-gray-600 mb-3">
                                  Please provide a reason for returning this ECDD to the compliance officer.
                                </p>
                                <textarea
                                  value={returnReason}
                                  onChange={(e) => setReturnReason(e.target.value)}
                                  placeholder="Enter reason for return..."
                                  className="w-full min-h-[100px] p-3 rounded-lg border border-gray-200 text-sm text-gray-700 mb-3"
                                />
                                <div className="flex gap-2 justify-end">
                                  <Button
                                    type="button"
                                    variant="outline"
                                    onClick={() => { setShowReturnModal(false); setReturnReason('') }}
                                    disabled={returningEcdd}
                                  >
                                    Cancel
                                  </Button>
                                  <Button
                                    type="button"
                                    className="bg-amber-600 hover:bg-amber-700 text-white"
                                    disabled={!returnReason.trim() || returningEcdd}
                                    onClick={handleReturnEcdd}
                                  >
                                    {returningEcdd ? 'Returning…' : 'Return ECDD'}
                                  </Button>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      ) : isApproved ? (
                        <div className="space-y-3">
                          {isApprovedSmr && (
                            <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-900">
                              <p className="font-medium mb-1">SMR Creation Approved</p>
                              <p className="text-xs">
                                Senior management has approved filing a Suspicious Matter Report. 
                                Complete the SMR filing process, then close this ECDD.
                              </p>
                            </div>
                          )}
                          {isApprovedMonitoring && (
                            <div className="rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-900">
                              <p className="font-medium mb-1">Enhanced Monitoring Approved</p>
                              <p className="text-xs">
                                Senior management has approved enhanced monitoring for this patron. 
                                Add the patron to the watchlist for periodic review. Close this ECDD when monitoring is in place.
                              </p>
                            </div>
                          )}
                          <Button
                            type="button"
                            className={isApprovedSmr ? 'bg-red-600 hover:bg-red-700 text-white' : 'bg-green-600 hover:bg-green-700 text-white'}
                            disabled={closingEcdd}
                            onClick={() => handleCloseEcdd()}
                          >
                            <CheckCircleIcon className="w-4 h-4 mr-1" />
                            {closingEcdd 
                              ? 'Closing…' 
                              : isApprovedSmr 
                                ? 'Mark SMR Filed & Close ECDD' 
                                : 'Mark Monitoring Active & Close ECDD'
                            }
                          </Button>
                          {approvalError && (
                            <p className="text-sm text-red-600">{approvalError}</p>
                          )}
                        </div>
                      ) : (
                        <>
                          <Button
                            type="button"
                            className="bg-psg-teal hover:bg-psg-teal-700"
                            disabled={!selectedDecision || !decisionRationale.trim() || submittingDecision}
                            onClick={handleSubmitForApproval}
                          >
                            <ShieldCheckIcon className="w-4 h-4 mr-1" />
                            {submittingDecision ? 'Submitting…' : 'Submit for Manager Approval'}
                          </Button>
                          <div className="text-sm text-gray-500 mt-2">
                            This will move the ECDD to &quot;Pending Approval&quot; for senior management sign-off.
                          </div>
                          {submitDecisionError && (
                            <p className="text-sm text-red-600 mt-2">{submitDecisionError}</p>
                          )}
                        </>
                      )}
                    </>
                  ) : (
                    <div className="py-6 text-center text-gray-500 text-sm">
                      {mandatoryItems.length - mandatoryDone} required item{mandatoryItems.length - mandatoryDone > 1 ? 's' : ''} remaining before you can record a decision.
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Right: Status & team, trigger, progress, details */}
        <div className="space-y-3">
          <Card>
            <CardContent className="p-4">
              <div className="text-sm font-bold text-psg-teal mb-3">Status & team</div>
              <div className="flex flex-wrap gap-2 items-center mb-3">
                <Badge bg="#e8f2f6" color="#0a5169" border="#b8d8e8">
                  {record.status === 'open' ? 'Open' : record.status === 'pending_approval' ? 'Pending Approval' : record.status === 'approved_monitoring' ? 'Approved — Monitoring' : record.status === 'approved_smr' ? 'Approved — SMR Pending' : 'Closed'}
                </Badge>
                {record.triggerType === 'pep' && <Badge bg="#fef2f2" color="#dc2626" border="#fecaca">Foreign PEP</Badge>}
                <span className="text-sm text-gray-500">Day {record.daysOpen}</span>
              </div>
              <div className="flex justify-between items-center py-2 border-t border-gray-100">
                <span className="text-sm text-gray-500">Risk score</span>
                <span className="text-sm font-bold tabular-nums" style={{ color: riskColor }}>
                  {record.risk}
                </span>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-gray-100">
                <span className="text-sm text-gray-500">KYC</span>
                <span className="text-sm font-medium text-gray-700 text-right">{record.kycStatus}</span>
              </div>
              <div className="pt-3">
                <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Team</div>
                <div className="flex flex-wrap gap-2 items-center">
                  {record.participants.map((p, i) => (
                    <span key={i} className="text-sm px-2.5 py-1 rounded-full bg-gray-100 text-gray-500">{p}</span>
                  ))}
                  <button type="button" className="text-sm font-semibold bg-transparent border-none cursor-pointer text-psg-teal hover:underline p-0">
                    + Add
                  </button>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex gap-2 items-center mb-3">
                <LockClosedIcon className="w-4 h-4 text-gray-500 flex-shrink-0" />
                <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Trigger</span>
              </div>
              <div className="space-y-3">
                <div>
                  <div className="text-xs font-medium text-gray-600 mb-0.5">Type</div>
                  <div className="text-sm font-semibold text-psg-teal leading-snug">{record.triggerDetail.label}</div>
                </div>
                <div>
                  <div className="text-xs font-medium text-gray-600 mb-0.5">Source</div>
                  <div className="text-sm text-gray-700">{record.triggerDetail.source}</div>
                </div>
                <div>
                  <div className="text-xs font-medium text-gray-600 mb-0.5">Date</div>
                  <div className="text-sm text-gray-700">{record.triggerDetail.date}</div>
                </div>
                <div>
                  <div className="text-xs font-medium text-gray-600 mb-0.5">Linked Alert</div>
                  <div className="text-sm text-gray-700 break-words">{record.triggerDetail.linkedAlert || '—'}</div>
                </div>
              </div>
              {record.pepStatus && (
                <div className="mt-3 pt-3 border-t border-gray-100">
                  <span className="text-sm font-semibold text-red-600 leading-snug">{record.pepStatus}</span>
                </div>
              )}
              <div className="border-t border-gray-100 pt-3 mt-3">
                <div className="flex justify-between items-center mb-1 gap-2">
                  <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Progress</span>
                  <span className={`text-lg font-bold tabular-nums ${allMandatoryDone ? 'text-green-600' : 'text-psg-teal'}`}>{pct}%</span>
                </div>
                <div className="h-2 rounded-full bg-gray-100 overflow-hidden mb-2">
                  <div
                    className="h-full rounded-full transition-all duration-300 bg-psg-teal"
                    style={{ width: `${pct}%`, backgroundColor: allMandatoryDone ? '#16a34a' : undefined }}
                  />
                </div>
                <div className="text-xs text-gray-500">
                  Items: <b className="text-gray-700">{doneItems}/{totalItems}</b>
                  <span className="mx-1.5">·</span>
                  <span className={allMandatoryDone ? 'text-green-600' : 'text-red-600'}>
                    Required: <b>{mandatoryDone}/{mandatoryItems.length}</b>
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="text-sm font-bold text-psg-teal mb-2">Details</div>
              {[
                ['Assigned To', record.assignedTo],
                ['Created', record.triggerDate],
                ['Linked Alert', record.linkedAlert || '—'],
                ['Linked SMR', record.linkedSmr || 'None'],
              ].map(([label, value], i) => (
                <div key={i} className="flex justify-between py-1.5 border-b border-gray-100 last:border-0">
                  <span className="text-sm text-gray-500">{label}</span>
                  <span className="text-sm font-medium text-gray-600">{value}</span>
                </div>
              ))}
            </CardContent>
          </Card>
        </div>
      </div>

      <PayoutHistoryModal
        open={payoutModalOpen}
        onClose={() => setPayoutModalOpen(false)}
        memberId={record.gamingMemberId}
        memberName={record.name}
        onExportAndAttach={() => {
          setBuckets((prev) =>
            prev.map((b) =>
              b.id === 'txn'
                ? {
                    ...b,
                    items: b.items.map((i) =>
                      i.id === 'txn_1' ? { ...i, docs: [...(i.docs || []), `Payout_History_${record.gamingMemberId || record.pid}.csv`] } : i
                    ),
                  }
                : b
            )
          )
        }}
      />
      <GamingSessionModal
        open={gamingSessionModalOpen}
        onClose={() => setGamingSessionModalOpen(false)}
        memberId={record.gamingMemberId}
        memberName={record.name}
        onExportAndAttach={() => {
          setBuckets((prev) =>
            prev.map((b) =>
              b.id === 'txn'
                ? {
                    ...b,
                    items: b.items.map((i) =>
                      i.id === 'txn_2' ? { ...i, docs: [...(i.docs || []), `Gaming_Sessions_${record.gamingMemberId || record.pid}.csv`] } : i
                    ),
                  }
                : b
            )
          )
        }}
      />
      <AddCustomItemModal
        open={customItemModalOpen}
        onClose={() => setCustomItemModalOpen(false)}
        onAdd={handleAddCustomItem}
      />
      <PatronScreeningToolModal
        open={patronScreeningToolOpen}
        onClose={() => {
          setPatronScreeningToolOpen(false)
          setSearchAuditRefreshKey((k) => k + 1)
        }}
        memberName={record.name}
        memberId={record.gamingMemberId}
      />
      <ScreenHelpPanel
        screenKey={ECDD_DETAIL_HELP_SCREEN_KEY}
        open={helpPanelOpen}
        onClose={() => setHelpPanelOpen(false)}
        sectionIdPrefix="ecdd-detail-help"
      />
    </div>
  )
}
